import { ajusteDeFocoEmCurso, painelDe, rotaDoPainel, SELETOR_CONTEUDO, SELETOR_PAINEL, SELETOR_PAINEL_ATIVO } from './foco';

/**
 * Review r31 visual (M1 a M6, B1 a B5): vigia global do foco no web e no portal. As rodadas 29 a 31 acharam, tela a tela,
 * a mesma classe de problema: o botão com o foco some (a linha removida, "Ativar" que vira formulário, "Concluir" que vira
 * selo, "Limpar" sem filtro) ou fica desabilitado, e o foco cai no body; quem usa teclado volta ao começo da página.
 *
 * Quando o elemento que tinha o foco sai da página ou se desabilita e o foco ficou no body, o vigia leva o foco ao primeiro
 * controle visível do ancestral mais próximo que continua na tela (o formulário que substituiu o botão, a lista que perdeu a
 * linha, a seção de filtros) e, sem nenhum, ao conteúdo (`#conteudo`). Os ajustes finos que já existem (`recuperarFoco`,
 * `carregarMais`, `limparFiltrosEFocarBusca`, a devolução do foco dos Modais) agem antes: o vigia espera `ESPERA_VIGIA_MS`
 * e só age se o foco continua perdido.
 *
 * Só depois de interação por teclado (com o toque, o celular não põe o foco no botão e um campo focado abriria o teclado da
 * tela) e só na mesma rota (troca de tela tem o seu próprio foco).
 *
 * Review r32 (visual M1 a M3, robustez B1 e B2, segurança B1):
 *  - a busca do alvo para no `#conteudo` (sem ele) ou na camada (`role=dialog`): nunca o menu, a barra do topo ou o "Pular";
 *  - com carregamento no lugar (`role=status`, `aria-busy`) e nenhum controle ainda, espera até `TETO_VIGIA_MS`;
 *  - botão só desabilitado por um instante (`disabled={mut.isPending}`): espera e devolve o foco a ele quando volta (r34: só
 *    com requisição em curso; desabilitado de vez conta como saído);
 *  - elemento escondido pela classe (a lista das conversas no celular) também conta como foco perdido;
 *  - nunca escolhe campo mascarado do modo discreto (o foco o revelaria, r10 visual M2);
 *  - `keydown` sem `key` (o do preenchimento automático do Chrome) é ignorado.
 *
 * Lote 19 (item 13, tela dividida): cada painel de conteúdo (`data-painel-conteudo`) é um `#conteudo`: a busca para nele, a
 * "mesma rota" é a do painel (o painel ao lado navega sem mudar a URL da janela) e o último recurso é o próprio painel.
 */
export const ESPERA_VIGIA_MS = 400;
/**
 * Teto da espera por carregamento; depois disso o vigia procura nos ancestrais de cima. Review r33 (visual B1): 10 s, o mesmo
 * de `ESPERA_RECARGA_MS` (estados.tsx); com 3 s, uma resposta de 3,5 s mandava o foco ao topo da página.
 */
export const TETO_VIGIA_MS = 10_000;
/**
 * Review r33 (robustez B1): botão desabilitado de passagem que segue na tela espera até ele voltar (o lugar do foco é dele:
 * o Tab do Chromium segue dali mesmo com o foco no body). Só depois deste limite, de segurança, o vigia escolhe outro.
 */
export const LIMITE_DESABILITADO_MS = 60_000;

/**
 * Review r34 (robustez M1): a maior parte dos botões desabilitados não volta (`disabled={!sujo}` no Salvar depois de salvar,
 * o "descer" no último item); esperar 60 s por eles deixava o foco no body. O limite longo só vale com requisição em curso
 * (a fonte é o React Query, registrada no `main.tsx` do web e do portal); sem nada em curso, o botão está desabilitado de vez
 * e o vigia escolhe o alvo como se ele tivesse saído. Sem fonte registrada, o vigia supõe que pode voltar (como antes).
 */
const fontesDeAtividade = new Set<() => boolean>();
export function registrarAtividadeDoVigia(fonte: () => boolean): () => void {
  fontesDeAtividade.add(fonte);
  return () => void fontesDeAtividade.delete(fonte);
}
const atividadeEmCurso = () => fontesDeAtividade.size === 0 || [...fontesDeAtividade].some((f) => f());
const FOCAVEIS = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CARREGANDO = '[role="status"][aria-live], [aria-busy="true"]';
const CAMADA = '[role="dialog"], [aria-modal="true"]';

const visivelPadrao = (el: Element) => (el as HTMLElement).offsetParent !== null || el === document.activeElement;

/** Os ancestrais onde o alvo pode estar: do pai para cima, parando antes do `#conteudo` e na camada (incluída). */
export function ancestraisDoFoco(el: Element, corpo: Element): Element[] {
  const lista: Element[] = [];
  for (let p = el.parentElement; p && p !== corpo && p !== corpo.ownerDocument.documentElement; p = p.parentElement) {
    if (p.matches(SELETOR_CONTEUDO) || p.matches(SELETOR_PAINEL)) break;
    lista.push(p);
    if (p.matches(CAMADA)) break;
  }
  return lista;
}

/** Sinal de "ainda carregando": o lugar tem um carregamento e nenhum controle; o vigia espera. */
export const ESPERAR = 'esperar' as const;

/**
 * Primeiro controle visível do ancestral vivo mais próximo; `ESPERAR` se o mais próximo com conteúdo só tem carregamento
 * (com `ignorarCarregando`, depois do teto, a busca segue aos ancestrais de cima). Nunca o campo mascarado nem o Fechar da
 * camada (r33 spec B2: um Enter a mais fecharia o Modal; a mesma regra do foco inicial, r6 visual B5); sem alvo dentro de
 * uma camada, a própria camada.
 */
export function alvoDoVigia(ancestrais: readonly Element[], visivel: (el: Element) => boolean = visivelPadrao, fora: Element | null = null, ignorarCarregando = false): HTMLElement | typeof ESPERAR | null {
  for (const a of ancestrais) {
    if (!a.isConnected) continue;
    const alvo = [...a.querySelectorAll<HTMLElement>(FOCAVEIS)].find((x) => x !== fora && visivel(x) && !x.hasAttribute('data-mascarado') && !x.hasAttribute('data-fechar') && !x.closest('[aria-hidden="true"], [inert]'));
    if (alvo) return alvo;
    if (!ignorarCarregando && a.querySelector(CARREGANDO)) return ESPERAR;
    if (a.matches(CAMADA)) {
      if (!a.hasAttribute('tabindex')) a.setAttribute('tabindex', '-1');
      return a as HTMLElement;
    }
  }
  return null;
}

export type SituacaoDoFoco = 'ok' | 'removido' | 'desabilitado' | 'escondido' | 'voltou';

/**
 * Como está o foco em relação ao último elemento focado: `ok` (o foco está em algum lugar, ou nada aconteceu com ele),
 * `removido`, `desabilitado`, `escondido` (a classe tirou da tela) ou `voltou` (estava desabilitado e voltou, com o foco no body).
 */
export function situacaoDoFoco(ultimo: Element | null, ativo: Element | null, corpo: Element, viuDesabilitado: boolean, visivel: (el: Element) => boolean = visivelPadrao): SituacaoDoFoco {
  if (!ultimo) return 'ok';
  const desabilitado = (ultimo as HTMLButtonElement).disabled === true;
  if (ativo && ativo !== corpo && !(ativo === ultimo && (desabilitado || !visivel(ultimo)))) return 'ok';
  if (!ultimo.isConnected) return 'removido';
  if (desabilitado) return 'desabilitado';
  if (!visivel(ultimo)) return 'escondido';
  return viuDesabilitado ? 'voltou' : 'ok';
}

export function instalarVigiaDeFoco(doc: Document = document, espera = ESPERA_VIGIA_MS, teto = TETO_VIGIA_MS, visivel: (el: Element) => boolean = visivelPadrao, limiteDesabilitado = LIMITE_DESABILITADO_MS): () => void {
  let ultimo: HTMLElement | null = null;
  let ancestrais: Element[] = [];
  let rota = '';
  let painel: HTMLElement | null = null;
  let teclado = false;
  let viuDesabilitado = false;
  let inicio = 0;
  let situacaoAnterior: SituacaoDoFoco = 'ok';
  let agendado: ReturnType<typeof setTimeout> | null = null;
  const caminho = () => (painel ? rotaDoPainel(painel) : (doc.defaultView?.location.pathname ?? ''));
  const agora = () => doc.defaultView?.performance.now() ?? Date.now();
  const encerrar = () => {
    agendado = null;
    inicio = 0;
    situacaoAnterior = 'ok';
    viuDesabilitado = false;
  };

  const aoFocar = (e: FocusEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t || t === doc.body || !t.parentElement) return;
    ultimo = t;
    ancestrais = ancestraisDoFoco(t, doc.body);
    painel = painelDe(t);
    rota = caminho();
    viuDesabilitado = false;
  };
  const aoTeclar = (e: KeyboardEvent) => {
    const k = typeof e.key === 'string' ? e.key : '';
    if (k === 'Tab' || k === 'Enter' || k === ' ' || k.startsWith('Arrow') || k === 'Escape') teclado = true;
  };
  const aoTocar = () => {
    teclado = false;
  };
  const conferir = () => {
    agendado = null;
    const reagendar = () => void (agendado = setTimeout(conferir, espera));
    // Um ajuste fino ainda em curso (a lista nova chegando) decide primeiro; o vigia confere de novo depois.
    if (ajusteDeFocoEmCurso()) return reagendar();
    if (!ultimo || !teclado || caminho() !== rota) return encerrar();
    const situacao = situacaoDoFoco(ultimo, doc.activeElement, doc.body, viuDesabilitado, visivel);
    if (situacao === 'ok') return encerrar();
    if (situacao === 'voltou') {
      ultimo.focus();
      return encerrar();
    }
    // Review r34 (robustez B1): o teto do carregamento conta de quando o botão desabilitado saiu ou se escondeu, não do começo.
    if (situacaoAnterior === 'desabilitado' && situacao !== 'desabilitado') inicio = agora();
    situacaoAnterior = situacao;
    const passou = agora() - inicio;
    if (situacao === 'desabilitado' && passou < limiteDesabilitado && atividadeEmCurso()) return reagendar();
    const alvo = alvoDoVigia(ancestrais, visivel, ultimo);
    if (alvo === ESPERAR && passou < teto) return reagendar();
    const final = alvo === ESPERAR ? alvoDoVigia(ancestrais, visivel, ultimo, true) : alvo;
    (final && final !== ESPERAR ? final : painel?.isConnected ? painel : (doc.querySelector<HTMLElement>(SELETOR_PAINEL_ATIVO) ?? doc.querySelector<HTMLElement>(SELETOR_CONTEUDO)))?.focus();
    encerrar();
  };
  const obs = new (doc.defaultView ?? window).MutationObserver(() => {
    if (!teclado || !ultimo) return;
    if ((ultimo as HTMLButtonElement).disabled) viuDesabilitado = true;
    if (agendado) return;
    // Barato: só agenda se o foco está no body (ou preso no próprio elemento desabilitado) ou se o elemento saiu da página.
    const ativo = doc.activeElement;
    if (ultimo.isConnected && ativo && ativo !== doc.body && !(ativo === ultimo && (ultimo as HTMLButtonElement).disabled)) return;
    inicio = agora();
    agendado = setTimeout(conferir, espera);
  });
  obs.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class', 'hidden'] });
  doc.addEventListener('focusin', aoFocar);
  doc.addEventListener('keydown', aoTeclar, true);
  doc.addEventListener('pointerdown', aoTocar, true);
  return () => {
    obs.disconnect();
    if (agendado) clearTimeout(agendado);
    doc.removeEventListener('focusin', aoFocar);
    doc.removeEventListener('keydown', aoTeclar, true);
    doc.removeEventListener('pointerdown', aoTocar, true);
  };
}
